import React, { useEffect, useRef } from 'react'
import { SERVICE_TITLE } from 'service'
import { featureFlags } from 'config/feature-flags'
import { isTotpCreateAllowed } from 'utils/plan'
import GoogleAuthUploadPanel from './GoogleAuthUploadPanel'
import TemplateDownloadSection from './TemplateForm/TemplateDownloadSection'
import TemplateImportAsLine from './TemplateForm/TemplateImportAsLine'
import { PRESETS } from './importReducer'
import { matchesAcceptedExt, toBrowserAccept } from './importUtils'
import styles from './style.module.scss'

// v2.5: UploadIcon 제거 (빈 드롭존에서 아이콘 미표시 — Figma 기준)

const FileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M4 3h8l4 4v10a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z"
      stroke="#1B3F6E"
      strokeWidth="1.5"
    />
    <path d="M12 3v4h4" stroke="#1B3F6E" strokeWidth="1.5" />
  </svg>
)

// 기본값을 상수로 둔다 — 매 렌더 새 객체면 이 객체를 deps 로 쓰는 하위 컴포넌트의 effect 가 매번 다시 돈다
const EMPTY_TEMPLATE_CATALOG = { status: 'idle', items: [], dataTypes: [] }

const ImportUploadStep = ({
  preset,
  fileName,
  dragging,
  dispatch,
  templateSchemas = {},
  onRetryTemplate,
  scannedQrs = [],
  // 파일 읽기·복호화 중 — 프리셋·파일 변경을 막는다(늦게 끝난 작업이 바뀐 상태 위에 결과를 쏟지 않게)
  isParsing = false,
  onQrScanningChange,
  // 템플릿 양식 프리셋(spec D3·D6): 카탈로그·가져오기 선택·헤더 판별 결과
  templateCatalog = EMPTY_TEMPLATE_CATALOG,
  selectedTemplateId = null,
  templateDetection = null,
  onRetryCatalog,
  t,
}) => {
  const fileRef = useRef()
  const dropZoneRef = useRef(null)
  const previousPresetId = useRef(preset.id)

  // 카드를 바꾼 직후 파일 올리는 곳이 보이도록 가장 가까운 만큼만 스크롤한다(spec D13). 모달을 처음 열 때는 하지 않는다 —
  // 이전 카드 id 와 비교해서 StrictMode 의 효과 이중 실행에도 안전하다. QR 카드는 드롭존 대신 패널 래퍼가 같은 ref 를 쓴다
  useEffect(() => {
    if (previousPresetId.current === preset.id) return
    previousPresetId.current = preset.id
    dropZoneRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [preset.id])

  const handlePreset = (p) => {
    if (isParsing) return
    dispatch({ type: 'SET_PRESET', payload: p })
  }

  const handleRetry = (e, presetId) => {
    e.stopPropagation() // 카드 선택 막음
    if (presetId === 'template') {
      if (onRetryCatalog) onRetryCatalog()
      return
    }
    if (onRetryTemplate) onRetryTemplate(presetId)
  }

  const getHasTemplateError = (presetId) => {
    if (presetId === 'officepassword') return false
    if (presetId === 'template') return templateCatalog.status === 'error'
    return templateSchemas[presetId]?.status === 'error'
  }

  const handleFile = (file) => {
    if (isParsing) return
    if (!file) return
    const accepted = preset.acceptedExts || [preset.ext]
    if (!matchesAcceptedExt(file.name, accepted)) {
      // eslint-disable-next-line no-alert
      alert(
        t('record.import.v2.onlyFileAllowed', {
          ext: accepted.map((e) => e.toUpperCase().slice(1)).join(', '),
        }),
      )
      return
    }
    dispatch({ type: 'SET_FILE', payload: { file, fileName: file.name } })
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    dispatch({ type: 'SET_DRAGGING', payload: true })
  }

  const handleDragLeave = () => {
    dispatch({ type: 'SET_DRAGGING', payload: false })
  }

  const handleDrop = (e) => {
    // 잠금 검사는 preventDefault 다음에 — 앞에서 return 하면 브라우저의 기본 드롭 동작이 남는다(T14b 가 defaultPrevented 를 단언)
    e.preventDefault()
    dispatch({ type: 'SET_DRAGGING', payload: false })
    if (isParsing) return
    handleFile(e.dataTransfer.files[0])
  }

  return (
    <div className={styles.uploadStep}>
      <p className={styles.uploadDesc}>{t('record.import.v2.uploadDesc')}</p>

      <div>
        <p className={styles.sectionTitle}>{t('record.import.v2.serviceSelect')}</p>
        <div className={styles.presetList}>
          {PRESETS.filter(
            (p) =>
              p.id !== 'googleauth' || (isTotpCreateAllowed() && featureFlags.totpGoogleImport),
          ).map((p) => {
            const hasTemplateError = getHasTemplateError(p.id)
            // 다운로드 영역(spec D19)은 카드 안이 아니라 카드의 형제로 둔다 — 카드 안에 넣으면 영역 안의 클릭이
            // 카드 onClick 으로 번져 SET_PRESET 이 나가고, 올려 둔 파일이 비워진다(importReducer.js SET_PRESET)
            return (
              <React.Fragment key={p.id}>
                <div
                  className={[styles.presetItem, preset.id === p.id ? styles.presetItemActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handlePreset(p)}
                  role="radio"
                  aria-checked={preset.id === p.id}
                  aria-disabled={isParsing}
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handlePreset(p)
                  }}
                >
                  <div
                    className={[
                      styles.presetRadio,
                      preset.id === p.id ? styles.presetRadioActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {preset.id === p.id && <div className={styles.presetRadioDot} />}
                  </div>
                  <div className={styles.presetContent}>
                    <div className={styles.presetHeader}>
                      <span className={styles.presetLabel}>
                        {t(p.labelKey, { service: SERVICE_TITLE })}
                      </span>
                      <span
                        className={[
                          styles.presetBadge,
                          preset.id === p.id ? styles.presetBadgeActive : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {p.ext.toUpperCase().slice(1)}
                      </span>
                    </div>
                    <p className={styles.presetDesc}>{t(p.descKey, { service: SERVICE_TITLE })}</p>
                    {hasTemplateError && (
                      <p className={styles.presetTemplateError}>
                        {t('record.import.v2.template.loadError')}
                        <button
                          type="button"
                          className={styles.presetRetryBtn}
                          onClick={(e) => handleRetry(e, p.id)}
                        >
                          {t('record.import.v2.template.retry')}
                        </button>
                      </p>
                    )}
                  </div>
                </div>
                {p.id === 'template' && preset.id === 'template' && (
                  <TemplateDownloadSection templateCatalog={templateCatalog} t={t} />
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {preset.id === 'googleauth' ? (
        <div ref={dropZoneRef}>
          <p className={styles.sectionTitle}>{t('record.totp.import.sectionTitle')}</p>
          <GoogleAuthUploadPanel
            preset={preset}
            scannedQrs={scannedQrs}
            dispatch={dispatch}
            t={t}
            isParsing={isParsing}
            onScanningChange={onQrScanningChange}
          />
        </div>
      ) : (
        <div>
          <p className={styles.sectionTitle}>{t('record.import.v2.fileUpload')}</p>
          <div
            ref={dropZoneRef}
            className={[styles.dropZone, dragging ? styles.dropZoneActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              if (isParsing) return
              fileRef.current?.click()
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter') fileRef.current?.click()
            }}
          >
            {fileName ? (
              <>
                <div className={styles.dropZoneIconFile}>
                  <FileIcon />
                </div>
                <p className={styles.dropZoneFileName}>{fileName}</p>
                <p className={styles.dropZoneHint}>{t('record.import.v2.changeFile')}</p>
              </>
            ) : (
              <>
                <p className={styles.dropZoneText}>{t('record.import.v2.dragOrClick')}</p>
                <p className={styles.dropZoneHint}>
                  {preset.allowedFormatKey
                    ? t(preset.allowedFormatKey)
                    : t('record.import.v2.allowedFormat', {
                        ext: (preset.acceptedExts || [preset.ext]).join(', '),
                      })}
                </p>
              </>
            )}
          </div>
          <input
            type="file"
            ref={fileRef}
            accept={toBrowserAccept(preset.acceptedExts || [preset.ext])}
            style={{ display: 'none' }}
            onChange={(e) => {
              handleFile(e.target.files[0])
              // 값을 비운다 — 같은 파일을 고쳐 다시 고르면 change 가 오지 않고, 보관한 File 은 디스크에서 바뀌면
              // 읽히지 않는다(Chrome 155 실측: 같은 경로 재선택 change 1회 그대로·보관 File 읽기 NotReadableError,
              // 값을 비우면 change 2회·읽기 성공)
              e.target.value = ''
            }}
          />
        </div>
      )}
      {preset.id === 'template' && fileName && (
        <TemplateImportAsLine
          templateCatalog={templateCatalog}
          selectedTemplateId={selectedTemplateId}
          templateDetection={templateDetection}
          isParsing={isParsing}
          dispatch={dispatch}
          t={t}
        />
      )}
    </div>
  )
}

export default ImportUploadStep
